import type { FC } from "react";
import { styled } from "@linaria/react";
import { MessageBody } from "@/components/chat/Message.ts";
import { MessageSide } from "@/types/message.ts";
import { type FlowItem, FlowItemType } from "@/types/flow.ts";
import { FlowMessageText } from "@/components/flowItems/FlowMessageText.tsx";
import { FlowMessageImage } from "@/components/flowItems/FlowMessageImage.tsx";
import { FlowMessageVideo } from "@/components/flowItems/FlowMessageVideo.tsx";
import { FlowMessageFile } from "@/components/flowItems/FlowMessageFile.tsx";
import { FlowMessageInformation } from "@/components/flowItems/FlowMessageInformation.tsx";
import { theme } from "@/themes/theme.ts";
import { HumanFriendlyStringDate } from "@/helpers/date.ts";
import {
  getQuestionIdFromFlowItem,
  hasRenderableQuestionParts,
  type QuestionsById,
} from "@/helpers/questionParts.ts";
import { QuestionPartsContent } from "@/components/flowItems/QuestionPartsContent.tsx";
import { clipboardShareSessionMessage } from "@/helpers/clipboard.ts";
import { useChatStore } from "@/stores/useChatStore.ts";
import { toast } from "sonner";
import { FlowSmartButtons } from "@/components/flowItems/FlowSmartButtons.tsx";

interface FlowMessageOptions {
  name?: string | null;
}

interface FlowMessageProps {
  side: MessageSide;
  bot?: boolean;
  ai?: boolean;
  flowItem: Partial<FlowItem>;
  options: FlowMessageOptions;
  preview?: boolean;
  layoutId?: string;
  questionsById?: QuestionsById;
}

// Copied from Eedi-family
const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  margin: 1rem 0;

  color: ${theme.dividerColor};
  text-transform: uppercase;
  font-size: 0.8rem;
  font-weight: 500;

  &:before,
  &:after {
    content: "";
    border-top: 2px solid ${theme.dividerColor};
    height: 0;
  }

  &:before {
    width: max(2rem, 12%);
  }
  &:after {
    flex-grow: 1;
  }
`;

/**
 *
 * @remarks Parent container should have a flex display so the right-sided messages can use flex-end
 */
export const FlowMessage: FC<FlowMessageProps> = ({
  flowItem,
  side,
  bot,
  ai,
  options,
  preview,
  layoutId,
  questionsById,
}) => {
  switch (flowItem.flowItemType) {
    // The 'ignore list'
    case FlowItemType.TextInput:
    case FlowItemType.UploadInput:
    case FlowItemType.DiagnosticQuestionRetryInput:
    case FlowItemType.DiagnosticQuestionAnswerInput:
    case FlowItemType.AutoRequestForHelp:
    case FlowItemType.TextInputExplanation:
    case FlowItemType.Metadata:
    case FlowItemType.Default:
      return null;

    // Custom body
    case FlowItemType.Information:
      return <FlowMessageInformation flowItem={flowItem} />;

    case FlowItemType.Divider:
      return <Divider />;

    default: {
      const questionId = getQuestionIdFromFlowItem(flowItem);
      const question = questionId ? questionsById?.get(questionId) : undefined;
      const shouldRenderQuestionParts =
        flowItem.flowItemType === FlowItemType.PinnedImage && hasRenderableQuestionParts(question);

      return (
        <MessageBody
          data-right={side === MessageSide.right}
          data-bot={bot}
          data-ai={ai}
          data-tooltip={
            !preview
              ? "Dispatched at: " + HumanFriendlyStringDate(flowItem.dateDispatched ?? "")
              : "Message preview, the student does not see this"
          }
          data-direction={side === MessageSide.right ? "left" : "right"}
          data-preview={preview}
          data-question={shouldRenderQuestionParts || undefined}
          layoutId={layoutId}
          id={flowItem.flowItemId ? `flow-message-${flowItem.flowItemId}` : undefined}
          onDoubleClick={() => {
            const { chats, activeChat } = useChatStore.getState();

            const openedChat = chats.get(activeChat.unwrapOrElse(() => 0));
            if (!openedChat) {
              toast.error("Chat message not linked to an active chat");
              return;
            }

            clipboardShareSessionMessage(
              openedChat.flowGeneratorSessionId,
              flowItem.flowItemId ?? 0,
            );
          }}
        >
          <MessageInner flowItem={flowItem} options={options} question={question} />
        </MessageBody>
      );
    }
  }
};

interface FlowInnerProps {
  flowItem: Partial<FlowItem>;
  options: FlowMessageOptions;
  question?: Parameters<typeof hasRenderableQuestionParts>[0];
}

const MessageInner: FC<FlowInnerProps> = ({ flowItem, options, question }) => {
  // Note for the future person reading this
  // Make sure the flow item is not already handled via switch inside <FlowMessage />
  switch (flowItem.flowItemType) {
    case FlowItemType.HighlightedText:
    case FlowItemType.DiagnosticQuestionRetryInput:
    case FlowItemType.Text:
    case FlowItemType.PinnedText:
    case FlowItemType.DiagnosticQuestionAnswerInput:
      return (
        <FlowMessageText name={options.name} message={flowItem.text ?? ""} flowItem={flowItem} />
      );

    case FlowItemType.RadioButtonInput:
      return <FlowSmartButtons flowItem={flowItem} />;

    case FlowItemType.Video:
    case FlowItemType.PinnedVideo:
      return <FlowMessageVideo src={flowItem.videoUrl ?? ""} />;

    case FlowItemType.PinnedImage:
      if (question && hasRenderableQuestionParts(question)) {
        return <QuestionPartsContent question={question} />;
      }

      return (
        <FlowMessageImage
          src={flowItem.imageUrl ?? ""}
          alt={flowItem.text || "Uploaded image"}
          message={flowItem.text ?? ""}
        />
      );

    case FlowItemType.Image:
      return (
        <FlowMessageImage
          src={flowItem.imageUrl ?? ""}
          alt={flowItem.text || "Uploaded image"}
          message={flowItem.text ?? ""}
        />
      );

    case FlowItemType.File:
      return <FlowMessageFile src={flowItem.fileUrl ?? ""} text={flowItem.text} />;

    default:
      console.warn("Unhandled flowItem type", flowItem.flowItemType, flowItem);
      return (
        <>
          {flowItem.text !== ""
            ? flowItem.text
            : "[Unhandled flowItem type: " + flowItem.flowItemType + "]"}
        </>
      );
  }
};
